/**
 * TEMPORÁRIO — etiqueta de CAUSA em cada busca de lista de atendimentos.
 *
 * Diagnóstico dos picos do Postgres no srv01 (03/nov/2026): o dono quer saber,
 * com certeza e não por coincidência de horário, o que faz o painel recarregar
 * as listas (principalmente Fechados, que reconta o histórico inteiro).
 *
 * Como funciona: tudo que pode causar uma busca MARCA o motivo no instante em
 * que acontece (aviso do tempo real, função do painel que mandou recarregar,
 * clique, abertura da lista). O navegador executa a causa e a busca na mesma
 * tarefa; a marca vale só até o fim dela (`setTimeout(0)` a limpa). A busca lê
 * a marca e manda no endereço (`_motivo`), que a API já grava no log de cada
 * requisição — o validador da lista descarta o campo extra, nada muda lá.
 *
 * Páginas 2+ de uma MESMA recarga rodam depois da anterior responder (outra
 * tarefa): herdam o motivo da 1ª página. Página pedida pela rolagem vem com
 * `fetchMore` no estado do TanStack — vira 'rolagem', sem dedução.
 *
 * Remover quando o diagnóstico acabar: este arquivo + o teste, e as chamadas em
 * socket.ts (dispatch), inbox-debug.ts (debugInvalidate, applyTicketCard,
 * patchTicket, invalidateServerTruthLists), hooks.ts (useTicketsList) e api.ts
 * (listTickets: opção `motivo` e os parâmetros `_motivo`/`_t`/`_u`).
 */

let doInstante: string | null = null;

/** Marca a causa do que está acontecendo nesta tarefa do navegador. */
export function marcarMotivo(motivo: string): void {
  doInstante = motivo;
  setTimeout(() => {
    if (doInstante === motivo) doInstante = null;
  }, 0);
}

/** Acrescenta um detalhe à causa atual (ex.: o aviso → qual função recarregou). */
export function detalharMotivo(detalhe: string): void {
  marcarMotivo(doInstante ? `${doInstante}|${detalhe}` : detalhe);
}

export function motivoDoInstante(): string | null {
  return doInstante;
}

// Eventos de ciclo do atendimento: o painel só consegue encaixar sem buscar
// quando o aviso traz o cartão pronto (`ticket`/`tickets`).
const CICLO = new Set([
  'ticket.queued',
  'ticket.transferred',
  'ticket.closed',
  'ticket.reopened',
  'ticket.tagged',
  'ticket.assigned',
]);

/** Rótulo de um aviso do tempo real: nome + se veio com cartão + origem. */
export function rotuloDoAviso(evento: string, payload: unknown): string {
  const p = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  let rotulo = `aviso:${evento}`;
  if (CICLO.has(evento)) rotulo += p.ticket || p.tickets ? ':com-cartao' : ':sem-cartao';
  if (typeof p.source === 'string') rotulo += `:${p.source}`;
  if (p.auto_inactivity === true) rotulo += ':inatividade';
  if (typeof p.automatico === 'string') rotulo += `:${p.automatico}`;
  return rotulo;
}

const daRecarga = new Map<string, string>();

/**
 * Causa de uma busca de lista.
 *
 * - 1ª página: a marca do instante; sem marca, 'primeira-carga' (lista ainda
 *   sem dados), 'busca-digitada' (a busca espera o usuário parar de digitar,
 *   então sai fora da tarefa da tecla) ou 'sem-motivo'.
 * - Página pedida pela rolagem: 'rolagem'.
 * - Demais páginas: continuação da recarga que começou na 1ª página.
 */
export function motivoDaBusca(
  chave: string,
  info: { temCursor: boolean; rolagem: boolean; semDados: boolean; comBusca: boolean },
): string {
  if (!info.temCursor) {
    const motivo =
      doInstante ??
      (info.semDados ? 'primeira-carga' : info.comBusca ? 'busca-digitada' : 'sem-motivo');
    daRecarga.set(chave, motivo);
    return motivo;
  }
  if (info.rolagem) return 'rolagem';
  return `continuacao|${daRecarga.get(chave) ?? 'sem-motivo'}`;
}

if (typeof document !== 'undefined') {
  // Fase de captura no document: roda antes dos handlers do React, então a
  // busca que o clique provocar já encontra a marca.
  document.addEventListener('click', () => marcarMotivo('clique'), true);
}
